import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { useActiveTimeTracker } from "@/hooks/useActiveTimeTracker";
import { usePageViewTracker } from "@/hooks/usePageViewTracker";
import { useNorthStarVisitor } from "@/hooks/useNorthStarVisitor";
import { useAppInstallTracker } from "@/hooks/useAppInstallTracker";
import { useAffiliateLinkTracker } from "@/hooks/useAffiliateLinkTracker";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import ScrollToTop from "@/components/ScrollToTop";
import AdminNotificationBell from "@/components/AdminNotificationBellLazy";
import ReviewPromptModal from "@/components/ReviewPromptModal";

// Lazy-loaded pages - each becomes its own chunk.
// Landing is also lazy: prerendered HTML keeps the LCP painted while JS loads,
// so deferring framer-motion + page code shrinks the initial mobile bundle.
const Landing = lazy(() => import("./pages/Landing"));
const About = lazy(() => import("./pages/About"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const LearnClassroom = lazy(() => import("./pages/LearnClassroom"));
const LearnHub = lazy(() => import("./pages/LearnHub"));
const StudyHubPurchase = lazy(() => import("./pages/StudyHubPurchase"));
const ExamBuilder = lazy(() => import("./pages/ExamBuilder"));
const ExamTake = lazy(() => import("./pages/ExamTake"));
const ExamBriefing = lazy(() => import("./pages/ExamBriefing"));
const ExamResults = lazy(() => import("./pages/ExamResults"));
const PastResults = lazy(() => import("./pages/PastResults"));
const WeakAreas = lazy(() => import("./pages/WeakAreas"));
const CommunityDifficulty = lazy(() => import("./pages/CommunityDifficulty"));
const ManageQuestions = lazy(() => import("./pages/ManageQuestions"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Licences = lazy(() => import("./pages/Licences"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const WorkbookSuccess = lazy(() => import("./pages/WorkbookSuccess"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminSecurity = lazy(() => import("./pages/AdminSecurity"));
const QuickExamSetup = lazy(() => import("./pages/QuickExamSetup"));
const Contact = lazy(() => import("./pages/Contact"));
const ReviewFlagged = lazy(() => import("./pages/ReviewFlagged"));
const ReviewWrong = lazy(() => import("./pages/ReviewWrong"));
const StudyNotes = lazy(() => import("./pages/StudyNotes"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ModuleLanding = lazy(() => import("./pages/ModuleLanding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const AdminReplyDraft = lazy(() => import("./pages/AdminReplyDrafts"));
const AdminReplyDraftList = lazy(() => import("./pages/AdminReplyDrafts").then((m) => ({ default: m.AdminReplyDraftList })));
const AdminInsights = lazy(() => import("./pages/AdminInsights"));
const R06CaseStudies = lazy(() => import("./pages/R06CaseStudies"));
const R06Resources = lazy(() => import("./pages/R06Resources"));
const R06Hub = lazy(() => import("./pages/R06Hub"));
const R06Readiness = lazy(() => import("./pages/R06Readiness"));
const ModuleReadiness = lazy(() => import("./pages/ModuleReadiness"));
const ModuleHub = lazy(() => import("./pages/ModuleHub"));
const ExamGuideExplanations = lazy(() => import("./pages/ExamGuideExplanations"));
const R06June2026 = lazy(() => import("./pages/R06June2026"));
const R06October2026 = lazy(() => import("./pages/R06October2026"));
const ExamTakeWritten = lazy(() => import("./pages/ExamTakeWritten"));
const ExamBriefingWritten = lazy(() => import("./pages/ExamBriefingWritten"));
const ExamResultsWritten = lazy(() => import("./pages/ExamResultsWritten"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightArticle = lazy(() => import("./pages/InsightArticle"));
const ExamsInProgress = lazy(() => import("./pages/ExamsInProgress"));
const AdminTransactions = lazy(() => import("./pages/AdminTransactions"));
const FAQ = lazy(() => import("./pages/FAQ"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ExamGuides = lazy(() => import("./pages/ExamGuides"));
const ExamGuideModule = lazy(() => import("./pages/ExamGuideModule"));
const AdminQuestionEditor = lazy(() => import("./pages/AdminQuestionEditor"));
const AdminNameChanger = lazy(() => import("./pages/AdminNameChanger"));
const AdminQuestionPreview = lazy(() => import("./pages/AdminQuestionPreview"));
const AdminQuestionStaging = lazy(() => import("./pages/AdminQuestionStaging"));
const AdminR0TwentyEditor = lazy(() => import("./pages/AdminR0TwentyEditor"));
const AdminUserMetrics = lazy(() => import("./pages/AdminUserMetrics"));
const AdminReporting = lazy(() => import("./pages/AdminReporting"));
const AdminArticles = lazy(() => import("./pages/AdminArticles"));
const AdminPlatformUsage = lazy(() => import("./pages/AdminPlatformUsage"));
const AdminR0TwentyUsage = lazy(() => import("./pages/AdminR0TwentyUsage"));
const AdminHealthCheck = lazy(() => import("./pages/AdminHealthCheck"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminRio = lazy(() => import("./pages/AdminRio"));
const AdminRepository = lazy(() => import("./pages/AdminRepository"));
const AdminAiUsage = lazy(() => import("./pages/AdminAiUsage"));
const AdminStudyHub = lazy(() => import("./pages/AdminStudyHub"));
const AdminLiveLog = lazy(() => import("./pages/AdminLiveLog"));
const FeedbackThanks = lazy(() => import("./pages/FeedbackThanks"));
const StudyRooms = lazy(() => import("./pages/StudyRooms"));
const CIIExamDates = lazy(() => import("./pages/CIIExamDates"));
const Ask = lazy(() => import("./pages/Ask"));
const R0Twenty = lazy(() => import("./pages/R0Twenty"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
// Career Hub (TalentHub) temporarily removed from the site — pages kept in repo for re-enabling later
const AdminQuestionReports = lazy(() => import("./pages/AdminQuestionReports"));
const R06PredictorPage = lazy(() => import("./pages/R06PredictorPage"));

const queryClient = new QueryClient();

// Map known CDN / link-preview / shortener hostnames to clean source labels
const REFERRER_HOST_MAP: Array<[RegExp, string]> = [
  [/(^|\.)statics\.teams\.cdn\.office\.net$/i, 'teams'],
  [/(^|\.)teams\.microsoft\.com$/i, 'teams'],
  [/(^|\.)teams\.cdn\.office\.net$/i, 'teams'],
  [/(^|\.)l\.facebook\.com$/i, 'facebook'],
  [/(^|\.)lm\.facebook\.com$/i, 'facebook'],
  [/(^|\.)facebook\.com$/i, 'facebook'],
  [/(^|\.)lnkd\.in$/i, 'linkedin'],
  [/(^|\.)linkedin\.com$/i, 'linkedin'],
  [/(^|\.)out\.reddit\.com$/i, 'reddit'],
  [/(^|\.)reddit\.com$/i, 'reddit'],
  [/(^|\.)t\.co$/i, 'twitter'],
  [/(^|\.)x\.com$/i, 'twitter'],
  [/(^|\.)twitter\.com$/i, 'twitter'],
  [/(^|\.)youtube\.com$/i, 'youtube'],
  [/(^|\.)youtu\.be$/i, 'youtube'],
  [/(^|\.)instagram\.com$/i, 'instagram'],
  [/(^|\.)l\.instagram\.com$/i, 'instagram'],
  [/(^|\.)t\.me$/i, 'telegram'],
  [/(^|\.)whatsapp\.com$/i, 'whatsapp'],
  [/(^|\.)bing\.com$/i, 'bing'],
  [/(^|\.)duckduckgo\.com$/i, 'duckduckgo'],
  [/(^|\.)google\./i, 'google'],
];

function mapReferrerHostToSource(hostname: string): string {
  const host = hostname.toLowerCase();
  for (const [re, label] of REFERRER_HOST_MAP) {
    if (re.test(host)) return label;
  }
  const stripped = host.replace(/^www\./, '');
  const parts = stripped.split('.');
  if (parts.length >= 2) return parts[parts.length - 2];
  return stripped;
}

function readStored(key: string): string | null {
  try { const v = localStorage.getItem(key); if (v) return v; } catch { /* ignore */ }
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function writeStored(key: string, value: string) {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}

function CaptureUtmSource() {
  // First-touch attribution: only capture if not already stored.
  // Uses localStorage so source survives across sessions/devices until signup.
  const existingSource = readStored('referral_source');
  const existingAttribution = readStored('attribution');

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const referrer = document.referrer;

  if (!existingSource) {
    if (utmSource) {
      writeStored('referral_source', utmSource.toLowerCase());
    } else if (params.get('gclid') || params.get('gad_source') || params.get('gbraid') || params.get('wbraid')) {
      writeStored('referral_source', 'google_ads');
    } else if (referrer && !referrer.includes(window.location.hostname)) {
      try {
        const host = new URL(referrer).hostname;
        writeStored('referral_source', mapReferrerHostToSource(host));
      } catch { /* ignore */ }
    } else {
      writeStored('referral_source', 'direct');
    }
  }

  if (!existingAttribution) {
    const attribution: Record<string, string> = {};
    const trackedKeys = ['gclid', 'gbraid', 'wbraid', 'gad_source', 'gad_campaignid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'msclkid', 'li_fat_id'];
    trackedKeys.forEach(k => {
      const v = params.get(k);
      if (v) attribution[k] = v;
    });
    if (referrer) attribution.referrer = referrer;
    attribution.landing_path = window.location.pathname + window.location.search;
    attribution.captured_at = new Date().toISOString();
    writeStored('attribution', JSON.stringify(attribution));
  }

  return null;
}

function AppRoutes() {
  useActiveTimeTracker();
  usePageViewTracker();
  useNorthStarVisitor();
  useAppInstallTracker();
  useAffiliateLinkTracker();
  return (
    <>
      <CaptureUtmSource />
      <ScrollToTop />
      <AdminNotificationBell />
      <ReviewPromptModal />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/r01-exam-practice" element={<ModuleLanding />} />
          <Route path="/r02-exam-practice" element={<ModuleLanding />} />
          <Route path="/r03-exam-practice" element={<ModuleLanding />} />
          <Route path="/r04-exam-practice" element={<ModuleLanding />} />
          <Route path="/r05-exam-practice" element={<ModuleLanding />} />
          <Route path="/r06-exam-practice" element={<ModuleLanding />} />
          <Route path="/cii-exam-dates" element={<CIIExamDates />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/exam-guides" element={<ExamGuides />} />
          <Route path="/r01-exam-guide" element={<ExamGuideModule />} />
          <Route path="/r02-exam-guide" element={<ExamGuideModule />} />
          <Route path="/r03-exam-guide" element={<ExamGuideModule />} />
          <Route path="/r04-exam-guide" element={<ExamGuideModule />} />
          <Route path="/r05-exam-guide" element={<ExamGuideModule />} />
          <Route path="/r06-exam-guide" element={<ExamGuideModule />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightArticle />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/exam/build" element={<ProtectedRoute><ExamBuilder /></ProtectedRoute>} />
          <Route path="/exam/quick" element={<ProtectedRoute><QuickExamSetup /></ProtectedRoute>} />
          <Route path="/exam/briefing" element={<ProtectedRoute><ExamBriefing /></ProtectedRoute>} />
          <Route path="/exam/take" element={<ProtectedRoute><ExamTake /></ProtectedRoute>} />
          <Route path="/exam/results" element={<ProtectedRoute><ExamResults /></ProtectedRoute>} />
          <Route path="/results" element={<ProtectedRoute><PastResults /></ProtectedRoute>} />
          <Route path="/weak-areas" element={<ProtectedRoute><WeakAreas /></ProtectedRoute>} />
          <Route path="/topic-difficulty" element={<ProtectedRoute><CommunityDifficulty /></ProtectedRoute>} />
          <Route path="/review-flagged" element={<ProtectedRoute><ReviewFlagged /></ProtectedRoute>} />
          <Route path="/review-wrong" element={<ProtectedRoute><ReviewWrong /></ProtectedRoute>} />
          <Route path="/study-notes" element={<ProtectedRoute><StudyNotes /></ProtectedRoute>} />
          <Route path="/learn" element={<ProtectedRoute><LearnHub /></ProtectedRoute>} />
          <Route path="/learn/purchase" element={<ProtectedRoute><StudyHubPurchase /></ProtectedRoute>} />
          <Route path="/learn/:module/:slug" element={<ProtectedRoute><LearnClassroom /></ProtectedRoute>} />
          <Route path="/ask" element={<Ask />} />
          <Route path="/ask/:threadId" element={<ProtectedRoute><Ask /></ProtectedRoute>} />
          <Route path="/r0-twenty" element={<R0Twenty />} />
          <Route path="/manage-questions" element={<ProtectedRoute><ManageQuestions /></ProtectedRoute>} />
          <Route path="/exams-in-progress" element={<ProtectedRoute><ExamsInProgress /></ProtectedRoute>} />
          <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
          <Route path="/licences" element={<ProtectedRoute><Licences /></ProtectedRoute>} />
          <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/workbook-success" element={<WorkbookSuccess />} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
          <Route path="/admin/chat" element={<AdminRoute><AdminChat /></AdminRoute>} />
          <Route path="/admin/reply-drafts" element={<AdminRoute><AdminReplyDraftList /></AdminRoute>} />
          <Route path="/admin/reply-drafts/:id" element={<AdminReplyDraft />} />
          <Route path="/admin/insights" element={<AdminRoute><AdminInsights /></AdminRoute>} />
          <Route path="/admin/transactions" element={<AdminRoute><AdminTransactions /></AdminRoute>} />
          <Route path="/admin/questions" element={<AdminRoute><AdminQuestionEditor /></AdminRoute>} />
          <Route path="/admin/name-changer" element={<AdminRoute><AdminNameChanger /></AdminRoute>} />
          <Route path="/admin/questions/preview/:module/:id" element={<AdminRoute><AdminQuestionPreview /></AdminRoute>} />
          <Route path="/admin/question-staging" element={<AdminRoute><AdminQuestionStaging /></AdminRoute>} />
          <Route path="/admin/r0twenty-questions" element={<AdminRoute><AdminR0TwentyEditor /></AdminRoute>} />


          <Route path="/admin/metrics" element={<AdminRoute><AdminUserMetrics /></AdminRoute>} />
          <Route path="/admin/reporting" element={<AdminRoute><AdminReporting /></AdminRoute>} />
          <Route path="/admin/articles" element={<AdminRoute><AdminArticles /></AdminRoute>} />
          <Route path="/admin/platform-usage" element={<AdminRoute><AdminPlatformUsage /></AdminRoute>} />
          <Route path="/admin/r0twenty-usage" element={<AdminRoute><AdminR0TwentyUsage /></AdminRoute>} />
          <Route path="/admin/question-reports" element={<AdminRoute><AdminQuestionReports /></AdminRoute>} />
          <Route path="/admin/health" element={<AdminRoute><AdminHealthCheck /></AdminRoute>} />
          <Route path="/admin/security" element={<AdminRoute><AdminSecurity /></AdminRoute>} />
          <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
          <Route path="/admin/rio" element={<AdminRoute><AdminRio /></AdminRoute>} />
          <Route path="/admin/repository" element={<AdminRoute><AdminRepository /></AdminRoute>} />
          <Route path="/admin/ai-usage" element={<AdminRoute><AdminAiUsage /></AdminRoute>} />
          <Route path="/admin/study-hub" element={<AdminRoute><AdminStudyHub /></AdminRoute>} />
          <Route path="/admin/live" element={<AdminRoute><AdminLiveLog /></AdminRoute>} />
          <Route path="/r06" element={<ProtectedRoute><R06Hub /></ProtectedRoute>} />
          <Route path="/readiness/:module" element={<ProtectedRoute><ModuleReadiness /></ProtectedRoute>} />
          <Route path="/module/:code" element={<ProtectedRoute><ModuleHub /></ProtectedRoute>} />
          <Route path="/module/:code/exam-guide/:edition" element={<ProtectedRoute><ExamGuideExplanations /></ProtectedRoute>} />
          <Route path="/r06/readiness" element={<ProtectedRoute><R06Readiness /></ProtectedRoute>} />
          <Route path="/r06/june-2026" element={<ProtectedRoute><R06June2026 /></ProtectedRoute>} />
          <Route path="/r06/october-2026" element={<ProtectedRoute><R06October2026 /></ProtectedRoute>} />
          <Route path="/r06/case-studies" element={<ProtectedRoute><R06CaseStudies /></ProtectedRoute>} />
          <Route path="/r06/resources" element={<ProtectedRoute><R06Resources /></ProtectedRoute>} />
          <Route path="/exam/written/briefing" element={<ProtectedRoute><ExamBriefingWritten /></ProtectedRoute>} />
          <Route path="/exam/written/take" element={<ProtectedRoute><ExamTakeWritten /></ProtectedRoute>} />
          <Route path="/exam/written/results" element={<ProtectedRoute><ExamResultsWritten /></ProtectedRoute>} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/feedback-thanks" element={<FeedbackThanks />} />
          <Route path="/feedback-thanks/provider" element={<FeedbackThanks />} />
          <Route path="/study-rooms" element={<Navigate to="/dashboard" replace />} />
          <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
          <Route path="/r06-predictor" element={<R06PredictorPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
